import Box from '@mui/material/Box';
import ButtonGroup from '@mui/material/ButtonGroup';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mdiMenuDown } from '@mdi/js';

import { Button } from 'common/components/Button';
import { Chip } from 'common/components/Chip';
import { Divider } from 'common/components/Divider';
import { DropdownMenu } from 'common/components/DropdownMenu';
import { Icon } from 'common/components/Icon';
import { IOfferEventDetailFragment } from 'graphql/types';
import { IGroupConditionRow } from 'offer/types';

import { EnrollParticipantsDrawer } from './EnrollParticipantsDrawer';

interface IProps {
    groupConditionRows: IGroupConditionRow[];
    loading?: boolean;
    offerEvent: IOfferEventDetailFragment;
    showBottomDivider?: boolean;
    onClickSubscribe?(groupId?: string): void;
}

export const GroupConditionEnrollments = ({
    groupConditionRows,
    loading,
    offerEvent,
    showBottomDivider,
    onClickSubscribe,
}: IProps) => {
    const [translate] = useTranslation();
    const [groupButtonLoading, setGroupButtonLoading] = useState<
        string | undefined
    >();
    const [enrollDrawerGroupId, setEnrollDrawerGroupId] = useState<
        string | undefined
    >();

    useEffect(() => {
        // Reset the group button loading if the loading state is false
        if (!!groupButtonLoading && !loading) {
            setGroupButtonLoading(undefined);
        }
    }, [loading, groupButtonLoading]);

    if (!groupConditionRows.length) return null;

    return (
        <>
            {groupConditionRows.map((row, index) => {
                const { canEnrollParticipants, canSubscribe, group, max } = row;
                // Manager-only fallback rows have no count from the backend.
                // Treat unknown as "we can't show availability" rather than
                // collapsing to 0 (which would falsely render `max` spots left).
                const hasKnownCount = row.count !== undefined;
                const groupSpotsLeft = hasKnownCount
                    ? (max || 0) - (row.count ?? 0)
                    : undefined;

                const hasBottomDivider =
                    index < groupConditionRows.length - 1 ||
                    !!showBottomDivider;

                // When the count is unknown (manager-only fallback row), we
                // can't determine fullness so we render the enroll button
                // optimistically; the backend rejects over-enrollment.
                const groupIsFull =
                    hasKnownCount && !!max && groupSpotsLeft === 0;

                const showRegisterButton = canSubscribe && !groupIsFull;
                const showEnrollDropdown =
                    canSubscribe && canEnrollParticipants && !groupIsFull;
                const showEnrollOnly =
                    !canSubscribe && canEnrollParticipants && !groupIsFull;

                const registerButton = (
                    <Button
                        color="primary"
                        disabled={loading}
                        loading={groupButtonLoading === group.id}
                        sx={{
                            flex: 'none',
                            maxWidth: 'fit-content',
                        }}
                        variant="contained"
                        onClick={() => {
                            setGroupButtonLoading(group.id);

                            onClickSubscribe?.(group.id);
                        }}
                    >
                        {translate('register')}
                    </Button>
                );

                const enrollButton = (
                    <Button
                        color="primary"
                        sx={{
                            flex: 'none',
                            maxWidth: 'fit-content',
                            ml: 'auto',
                        }}
                        variant="contained"
                        onClick={() => setEnrollDrawerGroupId(group.id)}
                    >
                        {translate('offerEventDetail.enrollParticipants')}
                    </Button>
                );

                return (
                    <Box key={group.id}>
                        <Stack
                            alignItems="center"
                            direction="row"
                            justifyContent="space-between"
                            spacing={2}
                            sx={{ width: '100%' }}
                        >
                            <Box>
                                <Typography
                                    fontWeight="bold"
                                    sx={{
                                        mb: !!max ? 0.5 : 0,
                                    }}
                                >
                                    {group.name}
                                </Typography>

                                {!!max && !!groupSpotsLeft && (
                                    <Chip
                                        label={translate('spotsLeft', {
                                            count: groupSpotsLeft,
                                        })}
                                    />
                                )}

                                {groupIsFull && (
                                    <Chip
                                        label={translate('full')}
                                        sx={{
                                            flex: 'none',
                                            ml: 'auto',
                                        }}
                                    />
                                )}
                            </Box>

                            {showEnrollDropdown && (
                                <ButtonGroup
                                    disableElevation
                                    color="primary"
                                    sx={{
                                        flex: 'none',
                                        maxWidth: 'fit-content',
                                        ml: 'auto',
                                    }}
                                    variant="contained"
                                >
                                    {registerButton}
                                    <DropdownMenu
                                        anchor={
                                            <Button
                                                aria-label={translate(
                                                    'actions'
                                                )}
                                                color="primary"
                                                sx={{ px: 1, py: 0 }}
                                                variant="contained"
                                            >
                                                <Icon
                                                    path={mdiMenuDown}
                                                    size="2.4rem"
                                                />
                                            </Button>
                                        }
                                        placement="bottom-end"
                                    >
                                        <MenuItem
                                            onClick={() =>
                                                setEnrollDrawerGroupId(group.id)
                                            }
                                        >
                                            {translate(
                                                'offerEventDetail.enrollParticipants'
                                            )}
                                        </MenuItem>
                                    </DropdownMenu>
                                </ButtonGroup>
                            )}

                            {showRegisterButton &&
                                !showEnrollDropdown &&
                                registerButton}

                            {showEnrollOnly && enrollButton}
                        </Stack>

                        {hasBottomDivider && <Divider mt={3} />}
                    </Box>
                );
            })}

            <EnrollParticipantsDrawer
                groupId={enrollDrawerGroupId}
                offerEvent={offerEvent}
                open={!!enrollDrawerGroupId}
                onOpenChange={(open) =>
                    !open && setEnrollDrawerGroupId(undefined)
                }
            />
        </>
    );
};
